import { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Form, Button } from 'react-bootstrap';
import { Search, ChevronDown } from 'lucide-react';
import type { ManualResponse, UpsertManualRequest, RegistryTreeResponse } from '../../../infrastructure/AIAgentRegistryClient';

interface UpsertManualModalProps {
  show: boolean;
  manual: ManualResponse | null;
  defaultCategory: string;
  registryTree?: RegistryTreeResponse | undefined;
  onHide: () => void;
  onSubmit: (data: UpsertManualRequest) => void;
  isSubmitting: boolean;
}

export const UpsertManualModal = ({
  show,
  manual,
  defaultCategory,
  registryTree,
  onHide,
  onSubmit,
  isSubmitting,
}: UpsertManualModalProps) => {
  const [pageId, setPageId] = useState('');
  const [pageSearch, setPageSearch] = useState('');
  const [isPageDropdownOpen, setIsPageDropdownOpen] = useState(false);
  const [name, setName] = useState('');
  const [category, setCategory] = useState(defaultCategory);
  const [description, setDescription] = useState('');
  const [bestPracticeIds, setBestPracticeIds] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const pageDropdownRef = useRef<HTMLDivElement>(null);

  const isEdit = !!manual;

  useEffect(() => {
    if (manual) {
      setPageId(manual.pageId);
      setName(manual.name);
      setCategory(manual.category);
      setDescription(manual.description);
      setBestPracticeIds(manual.bestPracticeExecutionIds?.join(', ') || '');
    } else {
      setPageId('');
      setName('');
      setCategory(defaultCategory);
      setDescription('');
      setBestPracticeIds('');
    }
    setPageSearch('');
    setIsPageDropdownOpen(false);
    setErrors({});
  }, [manual, defaultCategory, show]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageDropdownRef.current && !pageDropdownRef.current.contains(event.target as Node)) {
        setIsPageDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!pageId.trim()) newErrors['pageId'] = 'Notion Page ID is required';
    if (!name.trim()) newErrors['name'] = 'Name is required';
    if (!category.trim()) newErrors['category'] = 'Category is required';
    if (!description.trim()) newErrors['description'] = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data: UpsertManualRequest = {
      pageId,
      name,
      category,
      description,
    };

    if (isEdit && manual) data.id = manual.id;
    if (bestPracticeIds.trim()) {
      data.bestPracticeExecutionIds = bestPracticeIds.split(',').map((id) => id.trim()).filter(Boolean);
    }

    onSubmit(data);
  };

  const manualPages = registryTree?.manuals || [];
  const categories = useMemo(() => registryTree?.manuals.filter((m) => m.parentId === null) || [], [registryTree]);

  const filteredPages = useMemo(() => {
    if (!pageSearch.trim()) return manualPages;
    const searchLower = pageSearch.toLowerCase();
    return manualPages.filter((page) => page.title.toLowerCase().includes(searchLower));
  }, [manualPages, pageSearch]);

  const selectedPageTitle = useMemo(() => {
    if (!pageId) return '';
    const found = manualPages.find((p) => p.id === pageId);
    return found?.title || '';
  }, [pageId, manualPages]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Edit' : 'Create'} Manual</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Notion Page ID *</Form.Label>
            {manualPages.length > 0 ? (
              <div ref={pageDropdownRef} style={{ position: 'relative' }}>
                <div
                  className={`form-control d-flex align-items-center justify-content-between ${errors['pageId'] ? 'is-invalid' : ''}`}
                  style={{ cursor: 'pointer', minHeight: '38px' }}
                  onClick={() => setIsPageDropdownOpen(!isPageDropdownOpen)}
                >
                  <span className={selectedPageTitle ? '' : 'text-muted'}>
                    {selectedPageTitle || 'Select a Notion page...'}
                  </span>
                  <ChevronDown size={16} />
                </div>
                {isPageDropdownOpen && (
                  <div
                    className="border rounded bg-white shadow-sm"
                    style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: '250px', overflowY: 'auto' }}
                  >
                    <div className="p-2 border-bottom sticky-top bg-white">
                      <div className="input-group input-group-sm">
                        <span className="input-group-text"><Search size={14} /></span>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search pages..."
                          value={pageSearch}
                          onChange={(e) => setPageSearch(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    {filteredPages.length > 0 ? (
                      filteredPages.map((page) => (
                        <div
                          key={page.id}
                          className={`px-3 py-2 ${pageId === page.id ? 'bg-primary text-white' : ''}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setPageId(page.id);
                            if (!name.trim()) setName(page.title);
                            setPageSearch('');
                            setIsPageDropdownOpen(false);
                          }}
                          onMouseEnter={(e) => {
                            if (pageId !== page.id) e.currentTarget.classList.add('bg-light');
                          }}
                          onMouseLeave={(e) => e.currentTarget.classList.remove('bg-light')}
                        >
                          {page.title}
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-muted">No pages found</div>
                    )}
                  </div>
                )}
                {errors['pageId'] && <div className="invalid-feedback d-block">{errors['pageId']}</div>}
              </div>
            ) : (
              <>
                <Form.Control
                  type="text"
                  value={pageId}
                  onChange={(e) => setPageId(e.target.value)}
                  placeholder="Enter Notion page ID"
                  isInvalid={!!errors['pageId']}
                />
                <Form.Control.Feedback type="invalid">{errors['pageId']}</Form.Control.Feedback>
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., 주문 취소 매뉴얼"
              isInvalid={!!errors['name']}
            />
            <Form.Control.Feedback type="invalid">{errors['name']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Category *</Form.Label>
            <Form.Select value={category} onChange={(e) => setCategory(e.target.value)} isInvalid={!!errors['category']}>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.title.toLowerCase()}>{cat.title}</option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">{errors['category']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description *</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this manual..."
              isInvalid={!!errors['description']}
            />
            <Form.Control.Feedback type="invalid">{errors['description']}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Best Practice Execution IDs (comma-separated, optional)</Form.Label>
            <Form.Control
              type="text"
              value={bestPracticeIds}
              onChange={(e) => setBestPracticeIds(e.target.value)}
              placeholder="uuid1, uuid2, uuid3"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

